/**
 * SpaDetailPage Component
 * Phase D2.5: Luxury Spa Booking voice demo
 *
 * Calm, premium, voice-driven spa booking flow.
 * Shows spa treatments visually with local voice recognition.
 * Voice input is handled locally (spacebar does NOT open Ask Zaxia).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { Check, Mic, Calendar, Clock, Sparkles, X } from 'lucide-react';
import {
  spaTreatments,
  SPA_IMAGES,
  findTreatmentByVoice,
  parseDateTimeFromVoice,
  formatDuration,
  formatTimeForSpeech,
  formatDateForSpeech,
  type SpaTreatment,
} from '@/data/spaMenu';
import { zaxiaVoice, createSpeechRecognition, isSpeechRecognitionAvailable, type SpeechRecognitionEvent } from '@/services';
import { processVoiceInput, createVoiceContext, type VoiceAction } from '@/voice';
import type { CreateBookingRequest } from '@/api';

// =============================================================================
// TYPES
// =============================================================================

export interface VoiceHintConfig {
  title: string;
  hints: string[];
  variant?: 'default' | 'action' | 'confirm';
}

interface SpaDetailPageProps {
  isActive: boolean;
  onBack: () => void;
  onBookingComplete: (booking: CreateBookingRequest) => Promise<void>;
  onVoiceHintsChange?: (hints: VoiceHintConfig | null) => void;
}

type BookingStep = 'select-treatment' | 'select-datetime' | 'confirm';

interface SpaBooking {
  treatment: SpaTreatment | null;
  date: string | null; // YYYY-MM-DD
  time: string | null; // HH:MM
}

// =============================================================================
// HELPERS
// =============================================================================

function formatCurrency(amount: number): string {
  return `€${amount.toFixed(0)}`;
}

function generateAvailableDates(): string[] {
  const dates: string[] = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    dates.push(d.toISOString().split('T')[0]!);
  }
  return dates;
}

function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const todayStr = today.toISOString().split('T')[0]!;
  const tomorrowStr = tomorrow.toISOString().split('T')[0]!;
  if (dateStr === todayStr) return 'Today';
  if (dateStr === tomorrowStr) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// Available time slots
const TIME_SLOTS = [
  '09:00', '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00',
];

// =============================================================================
// COMPONENT
// =============================================================================

export function SpaDetailPage({
  isActive,
  onBack,
  onBookingComplete,
  onVoiceHintsChange,
}: SpaDetailPageProps) {
  const [focusedIndex, setFocusedIndex] = useState(0);
  // select-datetime sub-section navigation
  const [dtSection, setDtSection] = useState<'dates' | 'times'>('dates');
  const [focusedDateIndex, setFocusedDateIndex] = useState(0);
  const [focusedTimeIndex, setFocusedTimeIndex] = useState(4);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [bookingStep, setBookingStep] = useState<BookingStep>('select-treatment');
  const [booking, setBooking] = useState<SpaBooking>({
    treatment: null,
    date: null,
    time: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmFocusedButton, setConfirmFocusedButton] = useState<'confirm' | 'cancel'>('confirm');
  const containerRef = useRef<HTMLDivElement>(null);

  // Voice recognition state
  const [voiceState, setVoiceState] = useState<'idle' | 'listening' | 'processing'>('idle');
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceFeedback, setVoiceFeedback] = useState<string | null>(null);
  const recognitionRef = useRef<ReturnType<typeof createSpeechRecognition>>(null);
  const transcriptRef = useRef('');
  const confirmSpokenRef = useRef(false);
  // Timer that auto-navigates back after the booking confirmation TTS.
  // Tracked so it can be cancelled if the user navigates away first.
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const availableDates = useMemo(() => generateAvailableDates(), []);

  // Update global voice hints based on booking step
  useEffect(() => {
    if (bookingStep === 'select-treatment') {
      onVoiceHintsChange?.({
        title: 'SPA',
        hints: ['Swedish massage', 'Deep tissue', 'Facial'],
        variant: 'default',
      });
    } else if (bookingStep === 'select-datetime') {
      onVoiceHintsChange?.({
        title: 'Select time',
        hints: ['Tomorrow at 4pm', '3 o\'clock', 'Saturday'],
        variant: 'action',
      });
    } else if (bookingStep === 'confirm') {
      onVoiceHintsChange?.({
        title: 'Confirm booking',
        hints: ['Confirm', 'Cancel'],
        variant: 'confirm',
      });
    }
  }, [bookingStep, onVoiceHintsChange]);

  // Reset hints when leaving
  useEffect(() => {
    return () => {
      onVoiceHintsChange?.(null);
    };
  }, [onVoiceHintsChange]);

  // Auto-advance image slideshow
  useEffect(() => {
    if (SPA_IMAGES.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % SPA_IMAGES.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Speak helper — sets voiceFeedback and clears it when TTS finishes
  const speak = useCallback((text: string, onComplete?: () => void) => {
    setVoiceFeedback(text);
    zaxiaVoice.speak(text, {
      onEnd: () => {
        setVoiceFeedback(null);
        onComplete?.();
      },
    });
  }, []);

  // Speak confirmation when entering confirm step (only once)
  useEffect(() => {
    if (bookingStep === 'confirm' && booking.date && booking.time && booking.treatment && !confirmSpokenRef.current) {
      confirmSpokenRef.current = true;
      speak(`Your ${booking.treatment.name} is set for ${formatDateForSpeech(booking.date)} at ${formatTimeForSpeech(booking.time)}. Say confirm to book, or cancel to go back.`);
    }
    // Reset when leaving confirm step
    if (bookingStep !== 'confirm') {
      confirmSpokenRef.current = false;
    }
  }, [bookingStep, booking.date, booking.time, booking.treatment, speak]);

  // Select a treatment
  const selectTreatment = useCallback((treatment: SpaTreatment) => {
    setBooking(prev => ({ ...prev, treatment }));
    setBookingStep('select-datetime');
    setDtSection('dates');
    speak(`${treatment.name} selected. When would you like to book? Say a date and time, or use the calendar.`);
  }, [speak]);

  // Update date/time
  const updateDateTime = useCallback((date?: string, time?: string) => {
    setBooking(prev => {
      const newDate = date ?? prev.date;
      const newTime = time ?? prev.time;

      // If both date and time are set, move to confirm step
      // Speech is handled by useEffect above to avoid StrictMode double-speak
      if (newDate && newTime) {
        setTimeout(() => setBookingStep('confirm'), 0);
      }

      return {
        ...prev,
        date: newDate,
        time: newTime,
      };
    });
  }, []);

  // Submit booking
  const handleConfirmBooking = useCallback(async () => {
    if (!booking.treatment || !booking.date || !booking.time || isSubmitting) return;

    setIsSubmitting(true);

    try {
      console.log('[SPA] Booking values:', { date: booking.date, time: booking.time });
      const datetimeStr = `${booking.date}T${booking.time}:00`;
      console.log('[SPA] Datetime string:', datetimeStr);
      const datetime = new Date(datetimeStr);
      console.log('[SPA] Parsed datetime:', datetime.toISOString());

      const bookingRequest: CreateBookingRequest = {
        serviceId: `spa-${booking.treatment.id}`,
        serviceName: booking.treatment.name,
        category: 'SPA',
        datetime: datetime.toISOString(),
        durationMinutes: booking.treatment.durationMinutes,
        price: booking.treatment.price,
        location: 'Wellness Center, Level 2',
      };

      console.log('[SPA] Booking request:', bookingRequest);
      await onBookingComplete(bookingRequest);
      setShowConfirmation(true);

      speak(
        `Your ${booking.treatment.name} has been booked for ${formatDateForSpeech(booking.date)} at ${formatTimeForSpeech(booking.time)}. Enjoy your spa experience.`,
        () => {
          if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
          confirmTimerRef.current = setTimeout(() => {
            confirmTimerRef.current = null;
            setBooking({ treatment: null, date: null, time: null });
            setBookingStep('select-treatment');
            setShowConfirmation(false);
            onBack();
          }, 2000);
        }
      );
    } catch (error) {
      console.error('[ZAXIA] Spa booking failed:', error);
      speak('Sorry, there was a problem booking your treatment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [booking, isSubmitting, onBookingComplete, onBack, speak]);

  // Cancel booking / go back
  const handleCancel = useCallback(() => {
    if (bookingStep === 'confirm') {
      setBookingStep('select-datetime');
      setDtSection('dates');
    } else if (bookingStep === 'select-datetime') {
      setBooking(prev => ({ ...prev, treatment: null, date: null, time: null }));
      setBookingStep('select-treatment');
    } else {
      onBack();
    }
  }, [bookingStep, onBack]);

  // ==========================================================================
  // VOICE RECOGNITION (local, prevents Ask Zaxia from opening)
  // Phase D4.5.2: Uses ZaxiaVoiceEngine for command processing
  // ==========================================================================

  const handleVoiceAction = useCallback((action: VoiceAction, transcript: string) => {
    console.log('[SPA] Handling voice action:', action.type, transcript);

    // If the confirmation overlay is visible, navigation/global actions should
    // cancel the auto-dismiss timer and exit immediately rather than being
    // swallowed or processed as booking commands.
    if (showConfirmation) {
      const isNavigationAction =
        action.type === 'CLOSE' ||
        action.type === 'SPA_BACK' ||
        action.type === 'HOME' ||
        action.type.startsWith('OPEN_') ||
        action.type.startsWith('BOOK_');
      if (isNavigationAction) {
        if (confirmTimerRef.current) {
          clearTimeout(confirmTimerRef.current);
          confirmTimerRef.current = null;
        }
        setShowConfirmation(false);
        setBooking({ treatment: null, date: null, time: null });
        setBookingStep('select-treatment');
        onBack();
        return;
      }
      // Ignore other voice commands while showing the success overlay.
      return;
    }

    switch (action.type) {
      // Navigation: go back
      case 'CLOSE':
      case 'SPA_BACK':
        speak('Going back.');
        setTimeout(() => handleCancel(), 500);
        break;

      // Cancel booking
      case 'CANCEL':
      case 'SPA_CANCEL_BOOKING':
        if (booking.treatment) {
          setBooking({ treatment: null, date: null, time: null });
          setBookingStep('select-treatment');
          speak('Booking cancelled. Please select a treatment.');
        } else {
          speak('No booking to cancel.');
        }
        break;

      // Confirm booking (if at confirm step)
      case 'CONFIRM':
      case 'YES':
      case 'SPA_CONFIRM_BOOKING':
        if (bookingStep === 'confirm') {
          setVoiceFeedback('Confirming booking...');
          handleConfirmBooking();
        } else if (bookingStep === 'select-treatment') {
          speak("Please select a treatment first. Try saying 'Swedish massage' or 'facial'.");
        } else {
          speak("Please select a date and time first.");
        }
        break;

      // Select treatment
      case 'SPA_SELECT_TREATMENT': {
        const treatment = findTreatmentByVoice(action.payload.treatmentText);
        if (treatment) {
          selectTreatment(treatment);
          setVoiceFeedback(`Selected ${treatment.name}`);
        } else {
          speak("I couldn't find that treatment. Try saying 'Swedish massage' or 'facial'.");
        }
        break;
      }

      // Set date/time
      case 'SPA_SET_DATETIME': {
        const parsed = parseDateTimeFromVoice(action.payload.dateTimeText);
        if (parsed.date || parsed.time) {
          updateDateTime(parsed.date, parsed.time);
          const feedback = [];
          if (parsed.date) feedback.push(`Date: ${formatDateForSpeech(parsed.date)}`);
          if (parsed.time) feedback.push(`Time: ${formatTimeForSpeech(parsed.time)}`);
          setVoiceFeedback(feedback.join(' • '));
        } else {
          speak("I couldn't understand the date or time. Try saying 'tomorrow at 4 PM'.");
        }
        break;
      }

      // Unknown command - try to interpret based on step
      case 'UNKNOWN': {
        const lower = action.payload.transcript.toLowerCase();

        // Try treatment selection first
        const treatment = findTreatmentByVoice(lower);
        if (treatment) {
          selectTreatment(treatment);
          setVoiceFeedback(`Selected ${treatment.name}`);
          return;
        }

        // Try date/time parsing
        if (bookingStep === 'select-datetime' || bookingStep === 'confirm') {
          const parsed = parseDateTimeFromVoice(lower);
          if (parsed.date || parsed.time) {
            updateDateTime(parsed.date, parsed.time);
            const feedback = [];
            if (parsed.date) feedback.push(`Date: ${formatDateForSpeech(parsed.date)}`);
            if (parsed.time) feedback.push(`Time: ${formatTimeForSpeech(parsed.time)}`);
            setVoiceFeedback(feedback.join(' • '));
            return;
          }
        }

        // Fallback help based on step
        if (bookingStep === 'select-treatment') {
          speak("Please select a treatment. Try saying 'Swedish massage' or 'facial'.");
        } else if (bookingStep === 'select-datetime') {
          speak("Please say when you'd like to book. For example, 'tomorrow at 4 PM'.");
        } else {
          speak("Say 'confirm' to book or 'cancel' to start over.");
        }
        break;
      }

      // Global actions handled by parent
      default:
        console.log('[SPA] Unhandled action type:', action.type);
        break;
    }
  }, [booking.treatment, bookingStep, showConfirmation, onBack, selectTreatment, updateDateTime, handleConfirmBooking, handleCancel, speak]);

  const processVoiceCommand = useCallback((transcript: string) => {
    console.log('[SPA] Processing voice command:', transcript);

    // Use the central voice engine to parse the command
    const voiceContext = createVoiceContext('spa', {
      subContext: {
        spaBookingStep: bookingStep,
        spaHasTreatment: booking.treatment !== null,
      },
    });
    const action = processVoiceInput(transcript, voiceContext);

    handleVoiceAction(action, transcript);
  }, [bookingStep, booking.treatment, handleVoiceAction]);

  const startVoiceListening = useCallback(() => {
    if (!isSpeechRecognitionAvailable()) {
      setVoiceFeedback('Voice not supported');
      return;
    }

    // Cancel any ongoing speech
    zaxiaVoice.cancel();

    // Cancel any existing recognition
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    const recognition = createSpeechRecognition();
    if (!recognition) return;

    recognitionRef.current = recognition;
    setVoiceState('listening');
    setVoiceTranscript('');
    transcriptRef.current = '';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result?.[0]) {
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interimTranscript += result[0].transcript;
          }
        }
      }

      const currentTranscript = finalTranscript || interimTranscript;
      setVoiceTranscript(currentTranscript);
      transcriptRef.current = currentTranscript;
    };

    recognition.onerror = () => {
      setVoiceState('idle');
      setVoiceFeedback(null);
    };

    recognition.onend = () => {
      // Recognition ended naturally
    };

    recognition.start();
  }, []);

  const stopVoiceAndProcess = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // Ignore errors when stopping
      }
      recognitionRef.current = null;
    }

    const finalTranscript = transcriptRef.current.trim();

    if (!finalTranscript) {
      setVoiceState('idle');
      setVoiceFeedback(null);
      return;
    }

    setVoiceState('processing');
    processVoiceCommand(finalTranscript);

    // Clear feedback after a delay
    setTimeout(() => {
      setVoiceState('idle');
      setVoiceFeedback(null);
    }, 2500);
  }, [processVoiceCommand]);

  // Clear voice feedback after timeout
  useEffect(() => {
    if (voiceFeedback) {
      const timer = setTimeout(() => setVoiceFeedback(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [voiceFeedback]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // During the confirmation overlay, only allow Escape (exit early) and Space
      // (start voice, so the user can say commands like "go home"). All other
      // keys are suppressed so Enter / arrows don't re-trigger actions.
      if (showConfirmation) {
        if (e.key === 'Escape') {
          e.preventDefault();
          if (confirmTimerRef.current) {
            clearTimeout(confirmTimerRef.current);
            confirmTimerRef.current = null;
          }
          onBack();
        } else if (e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          if (voiceState === 'idle') startVoiceListening();
        }
        return;
      }

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          // In select-datetime, Escape steps back from times → dates before cancelling the step
          if (bookingStep === 'select-datetime' && dtSection === 'times') {
            setDtSection('dates');
          } else {
            handleCancel();
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (bookingStep === 'select-treatment') {
            setFocusedIndex(prev => (prev > 0 ? prev - 1 : spaTreatments.length - 1));
          } else if (bookingStep === 'select-datetime' && dtSection === 'times') {
            setDtSection('dates');
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (bookingStep === 'select-treatment') {
            setFocusedIndex(prev => (prev < spaTreatments.length - 1 ? prev + 1 : 0));
          } else if (bookingStep === 'select-datetime' && dtSection === 'dates') {
            setDtSection('times');
          }
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (bookingStep === 'select-datetime') {
            if (dtSection === 'dates') setFocusedDateIndex(prev => Math.max(0, prev - 1));
            else setFocusedTimeIndex(prev => Math.max(0, prev - 1));
          } else if (bookingStep === 'confirm') {
            setConfirmFocusedButton('confirm');
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (bookingStep === 'select-datetime') {
            if (dtSection === 'dates') setFocusedDateIndex(prev => Math.min(availableDates.length - 1, prev + 1));
            else setFocusedTimeIndex(prev => Math.min(TIME_SLOTS.length - 1, prev + 1));
          } else if (bookingStep === 'confirm') {
            setConfirmFocusedButton('cancel');
          }
          break;

        case 'Enter':
          e.preventDefault();
          if (bookingStep === 'select-treatment') {
            const treatment = spaTreatments[focusedIndex];
            if (treatment) selectTreatment(treatment);
          } else if (bookingStep === 'select-datetime') {
            if (dtSection === 'dates') {
              // Commit the focused date and move to time row
              const date = availableDates[focusedDateIndex];
              if (date) {
                setBooking(prev => ({ ...prev, date }));
                setDtSection('times');
                speak(`${formatDateDisplay(date)}. Now select a time.`);
              }
            } else {
              // Commit the focused time (+ current date) and advance to confirm
              const time = TIME_SLOTS[focusedTimeIndex];
              const date = booking.date ?? availableDates[focusedDateIndex];
              updateDateTime(date, time);
            }
          } else if (bookingStep === 'confirm') {
            if (confirmFocusedButton === 'confirm') handleConfirmBooking();
            else handleCancel();
          }
          break;

        case ' ':
          // Space starts voice input (prevents Ask Zaxia from opening)
          e.preventDefault();
          e.stopPropagation();
          if (voiceState === 'idle') {
            startVoiceListening();
          }
          break;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ' && voiceState === 'listening') {
        e.preventDefault();
        e.stopPropagation();
        stopVoiceAndProcess();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [
    isActive,
    focusedIndex,
    dtSection,
    focusedDateIndex,
    focusedTimeIndex,
    availableDates,
    updateDateTime,
    bookingStep,
    showConfirmation,
    voiceState,
    confirmFocusedButton,
    selectTreatment,
    handleConfirmBooking,
    handleCancel,
    onBack,
    startVoiceListening,
    stopVoiceAndProcess,
  ]);

  // Reset confirm button focus each time the user reaches the confirm step
  useEffect(() => {
    if (bookingStep === 'confirm') setConfirmFocusedButton('confirm');
  }, [bookingStep]);

  // Focus container on mount
  useEffect(() => {
    if (isActive && containerRef.current) {
      containerRef.current.focus();
    }
  }, [isActive]);

  // Cancel the post-confirmation auto-dismiss timer on unmount only.
  // This MUST be a separate effect with empty deps — if it were inside the
  // keyboard effect the cleanup would run on every dep change (e.g. when
  // showConfirmation flips to true), cancelling the timer immediately.
  useEffect(() => {
    return () => {
      if (confirmTimerRef.current) {
        clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = null;
      }
    };
  }, []);

  // Confirmation overlay
  if (showConfirmation) {
    return (
      <motion.div
        ref={containerRef}
        tabIndex={-1}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="h-full flex flex-col items-center justify-center p-8"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mb-6"
        >
          <Check className="w-10 h-10 text-emerald-400" />
        </motion.div>
        <h2 className="text-2xl font-light text-white mb-2">Booking Confirmed</h2>
        <p className="text-white/60 text-center">
          {booking.treatment?.name}
        </p>
        <p className="text-white/40 text-sm mt-2">
          {booking.date && formatDateForSpeech(booking.date)} at {booking.time && formatTimeForSpeech(booking.time)}
        </p>
        <p className="text-emerald-400/80 text-sm mt-4">
          {formatCurrency(booking.treatment?.price ?? 0)} • {formatDuration(booking.treatment?.durationMinutes ?? 0)}
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      tabIndex={-1}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="spa-detail h-full flex flex-col outline-none"
    >
      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Image slideshow - left side */}
        <div className="w-2/5 relative overflow-hidden flex-shrink-0">
          <AnimatePresence mode="wait">
            <img
              key={currentImageIndex}
              src={SPA_IMAGES[currentImageIndex]}
              alt="Spa"
              className="absolute inset-0 w-full h-full object-cover slide-img-fade"
            />
          </AnimatePresence>

          {/* Image indicators */}
          {SPA_IMAGES.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {SPA_IMAGES.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={clsx(
                    'h-1.5 rounded-full transition-all',
                    idx === currentImageIndex
                      ? 'bg-white/80 w-6'
                      : 'bg-white/30 w-1.5 hover:bg-white/50'
                  )}
                />
              ))}
            </div>
          )}
        </div>

        {/* Treatment selection / DateTime / Confirmation */}
        <div className="spa-content-panel flex-1 flex flex-col overflow-hidden px-6 py-4">
          <AnimatePresence mode="wait">
            {bookingStep === 'select-treatment' && (
              <motion.div
                key="treatment-select"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex-1 flex flex-col"
              >
                <h3 className="text-sm text-white/50 uppercase tracking-wider mb-4">Select a Treatment</h3>
                <div className="flex-1 space-y-3 overflow-y-auto hide-scrollbar">
                  {spaTreatments.map((treatment, idx) => {
                    const isFocused = focusedIndex === idx;
                    return (
                      <motion.div
                        key={treatment.id}
                        className={clsx(
                          'p-5 rounded-2xl border cursor-pointer transition-all',
                          isFocused
                            ? 'bg-emerald-500/10 border-emerald-500/30'
                            : 'bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.04]'
                        )}
                        animate={{ scale: isFocused ? 1.01 : 1 }}
                        onClick={() => {
                          setFocusedIndex(idx);
                          selectTreatment(treatment);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-base font-medium text-white">{treatment.name}</h4>
                            <p className="text-sm text-white/40 mt-1 line-clamp-2">{treatment.description}</p>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-lg font-light text-emerald-400">{formatCurrency(treatment.price)}</div>
                            <div className="text-xs text-white/40">{formatDuration(treatment.durationMinutes)}</div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {bookingStep === 'select-datetime' && (
              <motion.div
                key="datetime-select"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex-1 flex flex-col"
              >
                {/* Selected treatment summary */}
                <div className="mb-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium">{booking.treatment?.name}</div>
                      <div className="text-xs text-white/40 mt-0.5">{formatDuration(booking.treatment?.durationMinutes ?? 0)}</div>
                    </div>
                    <div className="text-emerald-400 font-light text-lg">{formatCurrency(booking.treatment?.price ?? 0)}</div>
                  </div>
                </div>

                <h3 className="text-sm text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2 flex-shrink-0">
                  <Calendar className="w-4 h-4" />
                  Select Date & Time
                </h3>

                {/* Date selection */}
                <div className="mb-4 flex-shrink-0">
                  <label className="text-xs text-white/40 mb-2 block">Date</label>
                  <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
                    {availableDates.map((date, idx) => (
                      <button
                        key={date}
                        onClick={() => { setFocusedDateIndex(idx); updateDateTime(date, undefined); setDtSection('times'); }}
                        className={clsx(
                          'px-4 py-2 rounded-xl whitespace-nowrap text-sm transition-all flex-shrink-0',
                          booking.date === date && idx === focusedDateIndex
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : booking.date === date
                            ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'
                            : idx === focusedDateIndex && dtSection === 'dates'
                            ? 'bg-white/20 text-white ring-2 ring-emerald-500 border border-transparent'
                            : 'bg-white/[0.04] text-white/60 border border-white/[0.04] hover:bg-white/[0.08]'
                        )}
                      >
                        {formatDateDisplay(date)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time slots */}
                <div className="min-h-0 overflow-y-auto">
                  <label className="text-xs text-white/40 mb-2 block flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Time
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {TIME_SLOTS.map((time, idx) => (
                      <button
                        key={time}
                        onClick={() => { setFocusedTimeIndex(idx); updateDateTime(booking.date ?? availableDates[0], time); }}
                        className={clsx(
                          'py-2.5 rounded-xl text-sm transition-all',
                          booking.time === time && idx === focusedTimeIndex
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : booking.time === time
                            ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'
                            : idx === focusedTimeIndex && dtSection === 'times'
                            ? 'bg-white/20 text-white ring-2 ring-emerald-500 border border-transparent'
                            : 'bg-white/[0.04] text-white/60 border border-white/[0.04] hover:bg-white/[0.08]'
                        )}
                      >
                        {formatTimeForSpeech(time)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick select */}
                <div className="mt-4 text-center">
                  <p className="text-xs text-white/30 mb-2">Or say "tomorrow at 4 PM"</p>
                </div>
              </motion.div>
            )}

            {bookingStep === 'confirm' && (
              <motion.div
                key="confirm"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="spa-confirm-step flex-1 flex flex-col items-center justify-center"
              >
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-6">
                  <Sparkles className="w-8 h-8 text-emerald-400" />
                </div>

                <h3 className="text-xl font-light text-white mb-6">Confirm Your Booking</h3>

                <div className="w-full max-w-sm space-y-4">
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    <div className="text-center">
                      <div className="text-white font-medium text-lg">{booking.treatment?.name}</div>
                      <div className="text-white/40 text-sm mt-1">{formatDuration(booking.treatment?.durationMinutes ?? 0)}</div>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-white/60">
                        <Calendar className="w-4 h-4" />
                        {booking.date && formatDateForSpeech(booking.date)}
                      </div>
                      <div className="flex items-center gap-2 text-white/60">
                        <Clock className="w-4 h-4" />
                        {booking.time && formatTimeForSpeech(booking.time)}
                      </div>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center justify-between">
                      <span className="text-white/60">Total</span>
                      <span className="text-emerald-400 text-xl font-light">{formatCurrency(booking.treatment?.price ?? 0)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-8">
                  <button
                    onClick={handleCancel}
                    className={clsx(
                      'px-6 py-2.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-white/60 transition-colors flex items-center gap-2',
                      confirmFocusedButton === 'cancel' && 'ring-2 ring-white/50 bg-white/[0.08]'
                    )}
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmBooking}
                    disabled={isSubmitting}
                    className={clsx(
                      'px-8 py-2.5 rounded-xl transition-all flex items-center gap-2',
                      isSubmitting
                        ? 'bg-white/[0.08] text-white/40 cursor-not-allowed'
                        : confirmFocusedButton === 'confirm'
                        ? 'bg-emerald-500/30 text-emerald-300 ring-2 ring-emerald-400'
                        : 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400'
                    )}
                  >
                    {isSubmitting ? (
                      <>Booking...</>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Confirm Booking
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer with voice indicator */}
      <div className="px-6 py-3 border-t border-white/[0.04]">
        {/* Voice indicator - shows when listening or has feedback */}
        <AnimatePresence>
          {(voiceState !== 'idle' || voiceFeedback) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="flex items-center justify-center gap-3 mb-3"
            >
              {voiceState === 'listening' && (
                <>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1.2, ease: 'easeInOut' }}
                    className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center"
                  >
                    <Mic className="w-4 h-4 text-emerald-400" />
                  </motion.div>
                  <span className="text-sm text-white/60">
                    {voiceTranscript || 'Listening...'}
                  </span>
                </>
              )}
              {voiceState === 'processing' && (
                <span className="text-sm text-white/60">Processing...</span>
              )}
              {voiceState === 'idle' && voiceFeedback && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-sm text-emerald-400"
                >
                  {voiceFeedback}
                </motion.span>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </motion.div>
  );
}
